import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { pm2ConfigStore } from '@pm2/store';
import { useNotifier } from '@provider/notifier';
import { useUserIdentityState } from '@provider/user-identity';
import { getInternalKeyForFavouriteDescriptor } from '../util.get-internal-key.js';
import { getFavouritesQuery } from './get-favourites.query.js';
import { selectFullDescriptor } from './selector.full-descriptor.js';
import { selectIsFavourite } from './selector.is-favourite.js';
import { postFavouriteRequest, deleteFavouriteRequest } from './toggle-favourite.request.js';

function useToggleFavouriteMutation(partialDescriptor) {
  const client = useQueryClient();
  const user = useUserIdentityState(state => state.user);
  const notifier = useNotifier();
  const configStore = pm2ConfigStore.useStore();
  const {
    data: descriptor
  } = useQuery(getFavouritesQuery.useQueryConfig({
    queryConfig: {
      select: selectFullDescriptor(partialDescriptor)
    }
  }));
  const {
    data: isFavourite
  } = useQuery(getFavouritesQuery.useQueryConfig({
    queryConfig: {
      select: selectIsFavourite(descriptor)
    }
  }));
  const addMutation = useMutation(addFavouriteMutation(client, configStore, notifier, user, descriptor));
  const deleteMutation = useMutation(removeFavouriteMutation(client, configStore, notifier, user, descriptor));
  return {
    addMutation,
    deleteMutation,
    isFavourite
  };
}
const addFavouriteMutation = (client, configStore, notifier, user, descriptor) => {
  return {
    mutationKey: ["user-area/favourites/add", user?.id, getInternalKeyForFavouriteDescriptor(descriptor)],
    mutationFn: async () => {
      if (!user) {
        throw new Error("addFavouriteMutation(): Cannot post favourite when no user is resolved");
      }
      if (!descriptor) {
        throw new Error("addFavouriteMutation(): Cannot post favourite when no descriptor is provided");
      }
      return postFavouriteRequest(configStore.getState().config.id, user, descriptor);
    },
    onMutate: async () => {
      if (!descriptor) {
        return;
      }
      client.setQueryData(getFavouritesQuery.queryKey({
        user,
        configStore,
        portalId: configStore.getState().config.id
      }), previous => [...(previous ?? []), descriptor]);
      await client.cancelQueries({
        queryKey: getFavouritesQuery.queryKey({
          user,
          configStore,
          portalId: configStore.getState().config.id
        })
      });
      return {
        optimisticDescriptor: descriptor
      };
    },
    onSuccess: (data, input, ctx) => {
      client.setQueryData(getFavouritesQuery.queryKey({
        user,
        configStore,
        portalId: configStore.getState().config.id
      }), previous => previous?.map(it => getInternalKeyForFavouriteDescriptor(it) === getInternalKeyForFavouriteDescriptor(ctx?.optimisticDescriptor) ? data : it));
    },
    onError: (error, input, ctx) => {
      client.setQueryData(getFavouritesQuery.queryKey({
        user,
        configStore,
        portalId: configStore.getState().config.id
      }), previous => previous?.filter(it => getInternalKeyForFavouriteDescriptor(it) !== getInternalKeyForFavouriteDescriptor(ctx?.optimisticDescriptor)));
      const descriptorLabel = ctx?.optimisticDescriptor.type === "page" ? "siden" : "forløbet";
      notifier.notify({
        id: "user-area/favourites/add/error",
        type: "error",
        body: `Noget gik galt da vi forsøgte at markere ${descriptorLabel} som favorit. Prøv igen om lidt.`
      });
    }
  };
};
const removeFavouriteMutation = (client, configStore, notifier, user, descriptor) => {
  return {
    mutationKey: ["user-area/favourites/remove", user?.id, getInternalKeyForFavouriteDescriptor(descriptor)],
    mutationFn: async () => {
      if (!user) {
        throw new Error("removeFavouriteMutation(): Cannot post favourite when no user is resolved");
      }
      if (!descriptor) {
        throw new Error("removeFavouriteMutation(): Cannot post favourite when no descriptor is provided");
      }
      return deleteFavouriteRequest(descriptor);
    },
    onMutate: async () => {
      const descriptors = client.getQueryData(getFavouritesQuery.queryKey({
        user,
        configStore,
        portalId: configStore.getState().config.id
      }));
      const descriptorIndex = descriptors?.findIndex(it => getInternalKeyForFavouriteDescriptor(it) === getInternalKeyForFavouriteDescriptor(descriptor)) ?? -1;
      client.setQueryData(getFavouritesQuery.queryKey({
        user,
        configStore,
        portalId: configStore.getState().config.id
      }), previous => previous?.filter(it => getInternalKeyForFavouriteDescriptor(it) !== getInternalKeyForFavouriteDescriptor(descriptor)));
      await client.cancelQueries({
        queryKey: getFavouritesQuery.queryKey({
          user,
          configStore,
          portalId: configStore.getState().config.id
        })
      });
      return {
        descriptor,
        index: descriptorIndex
      };
    },
    onError: (error, input, ctx) => {
      client.setQueryData(getFavouritesQuery.queryKey({
        user,
        configStore,
        portalId: configStore.getState().config.id
      }), previous => {
        if (!ctx?.descriptor) {
          return previous;
        }
        const next = [...(previous ?? [])];
        next?.splice(ctx.index, 0, ctx.descriptor);
        return next;
      });
      notifier.notify({
        id: "user-area/favourites/remove/error",
        type: "error",
        body: `Noget gik galt da vi forsøgte at fjerne din favorit. Prøv igen om lidt.`
      });
    }
  };
};

export { useToggleFavouriteMutation };
//# sourceMappingURL=toggle-favourite.mutation.js.map
